import { useMutation } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import type {
    TApiODIN,
    TExtractionType,
    TRowType,
    TTableLocation,
    TRequestParamsSaveMappings,
    TRequestParamsSaveLabelset,
} from "@/apis/odin/apiODIN";
import type { TSliceNotifications } from "@/store/notifications/sliceNotifications";
import { extractionKeys, projectKeys } from "@/queries/queryKeys";
import { apiErrorDetail } from "@/apis/apiError";
import { aiGateRefusalReason } from "./aiGateRefusal";

export const createExtractionMutationHooks = ({
    apiODIN,
    queryClient,
    addNotificationWithDefaultTimeout,
}: {
    apiODIN: TApiODIN;
    queryClient: QueryClient;
    addNotificationWithDefaultTimeout: TSliceNotifications["addNotificationWithDefaultTimeout"];
}) => {
    const extractionMutationKeys = {
        changeTable: ["extraction", "changeTable"] as const,
        saveMappings: ["extraction", "saveMappings"] as const,
        saveLabelset: ["extraction", "saveLabelset"] as const,
        discard: ["extraction", "discard"] as const,
        updateType: ["extraction", "updateType"] as const,
        changeRowType: ["extraction", "changeRowType"] as const,
        flipRowSign: ["extraction", "flipRowSign"] as const,
        setDocumentTargetRow: ["extraction", "setDocumentTargetRow"] as const,
        editCellValues: ["extraction", "editCellValues"] as const,
        revertCellValues: ["extraction", "revertCellValues"] as const,
        resetRowChanges: ["extraction", "resetRowChanges"] as const,
        setColumnState: ["extraction", "setColumnState"] as const,
        batchSetColumnState: ["extraction", "batchSetColumnState"] as const,
        convertToDraft: ["extraction", "convertToDraft"] as const,
        llmReExtract: ["extraction", "llmReExtract"] as const,
        dismissOutliers: ["extraction", "dismissOutliers"] as const,
        dismissLlmDiagnoses: ["extraction", "dismissLlmDiagnoses"] as const,
    };

    const invalidateExtraction = ({
        extractionId,
        includeLabelset = false,
        includeExtractionType = false,
        includeConflicts = false,
    }: {
        extractionId: number;
        includeLabelset?: boolean;
        includeExtractionType?: boolean;
        includeConflicts?: boolean;
    }) => {
        void queryClient.invalidateQueries({
            queryKey: extractionKeys.raw(extractionId),
        });
        void queryClient.invalidateQueries({
            queryKey: extractionKeys.standardized(extractionId),
        });
        void queryClient.invalidateQueries({
            queryKey: extractionKeys.overlappingPeriods(extractionId),
        });
        if (includeLabelset) {
            void queryClient.invalidateQueries({
                queryKey: extractionKeys.labelset(extractionId),
            });
        }
        if (includeExtractionType) {
            void queryClient.invalidateQueries({
                queryKey: extractionKeys.extractionType(extractionId),
            });
        }
        if (includeConflicts) {
            void queryClient.invalidateQueries({
                queryKey: extractionKeys.conflicts(extractionId),
            });
        }
    };

    const useChangeTableMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.changeTable,
            mutationFn: async ({
                extractionId,
                table,
            }: {
                extractionId: number;
                table: TTableLocation;
            }) => {
                const response = await apiODIN.changeExtractionTable({
                    extractionId,
                    table,
                });
                return response.data;
            },
            onSuccess: (_data, { extractionId }) => {
                invalidateExtraction({
                    extractionId,
                    includeLabelset: true,
                    includeExtractionType: true,
                });
            },
            onError: (error: unknown) => {
                const aiRefusal = aiGateRefusalReason(error);
                const refused = aiRefusal !== null;
                addNotificationWithDefaultTimeout({
                    title: refused
                        ? "Table change not possible"
                        : "Failed to change extraction table",
                    message: aiRefusal ?? undefined,
                    kind: "toast",
                    severity: refused ? "warning" : "danger",
                });
            },
        });

    const useSaveMappingsMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.saveMappings,
            mutationFn: async (params: TRequestParamsSaveMappings) => {
                const response = await apiODIN.saveMappings(params);
                return response.data;
            },
            onSuccess: (_data, { extractionId }) => {
                invalidateExtraction({ extractionId });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to save mappings",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useSaveLabelsetMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.saveLabelset,
            mutationFn: async (params: TRequestParamsSaveLabelset) => {
                const response = await apiODIN.saveLabelset(params);
                return response.data;
            },
            onSuccess: (_data, { extractionId }) => {
                // Standardized + overlapping periods are derived from the
                // mappings, so mark them stale (fire-and-forget) to refetch the
                // next time those views are opened.
                void queryClient.invalidateQueries({
                    queryKey: extractionKeys.standardized(extractionId),
                });
                void queryClient.invalidateQueries({
                    queryKey: extractionKeys.overlappingPeriods(extractionId),
                });
                // Return (await) the refetch of the queries that drive the
                // mappings page so the mutation stays "pending" until the fresh
                // grid + labelset data lands. This flow has no optimistic
                // update, so keeping it pending keeps the loading overlay up and
                // avoids a window where the old values briefly show after the
                // overlay disappears (the POST resolves before the GET lands).
                return Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: extractionKeys.raw(extractionId),
                    }),
                    queryClient.invalidateQueries({
                        queryKey: extractionKeys.labelset(extractionId),
                    }),
                ]);
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to save labelset mappings",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useDiscardExtractionMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.discard,
            mutationFn: async ({
                projectId,
                databaseId,
                extractionId,
            }: {
                projectId: number;
                databaseId: number;
                extractionId: number;
            }) => {
                await apiODIN.deleteExtractions({
                    extractionIds: [extractionId],
                    projectId,
                    databaseId,
                });
            },
            onSuccess: (_data, { extractionId }) => {
                invalidateExtraction({
                    extractionId,
                    includeLabelset: true,
                    includeExtractionType: true,
                    includeConflicts: true,
                });
                void queryClient.invalidateQueries({
                    queryKey: projectKeys.all,
                });
            },
            onError: (error: unknown) => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to discard extraction",
                    message: apiErrorDetail(error) ?? undefined,
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useUpdateExtractionTypeMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.updateType,
            mutationFn: async ({
                extractionId,
                extractionType,
            }: {
                extractionId: number;
                extractionType: TExtractionType;
            }) => {
                await apiODIN.updateExtractionType({
                    extractionId,
                    extractionType,
                });
            },
            onSuccess: (_data, { extractionId }) => {
                invalidateExtraction({
                    extractionId,
                    includeExtractionType: true,
                });
            },
        });

    const useChangeRowTypeMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.changeRowType,
            mutationFn: async ({
                extractionId,
                rowIds,
                rowType,
            }: {
                extractionId: number;
                rowIds: number[];
                rowType: TRowType;
            }) => {
                await apiODIN.updateRowType({ extractionId, rowIds, rowType });
            },
            onSuccess: (_data, { extractionId }) => {
                invalidateExtraction({ extractionId });
            },
        });

    const useFlipRowSignMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.flipRowSign,
            mutationFn: async ({
                extractionId,
                rowIds,
                flipped,
            }: {
                extractionId: number;
                rowIds: number[];
                flipped: boolean;
            }) => {
                await apiODIN.flipRowSign({ extractionId, rowIds, flipped });
            },
            onSuccess: (_data, { extractionId }) => {
                invalidateExtraction({ extractionId });
            },
        });

    const useSetDocumentTargetRowMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.setDocumentTargetRow,
            mutationFn: async ({
                extractionId,
                rowId,
            }: {
                extractionId: number;
                rowId: number | null;
            }) => {
                await apiODIN.setDocumentTargetRow({ extractionId, rowId });
            },
            onSuccess: (_data, { extractionId }) => {
                invalidateExtraction({ extractionId });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    kind: "toast",
                    title: "Failed to set document target row",
                    severity: "danger",
                });
            },
        });

    const useEditCellValuesMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.editCellValues,
            mutationFn: async ({
                extractionId,
                cells,
            }: {
                extractionId: number;
                cells: {
                    rowId: number;
                    columnId: number;
                    amount: number | null;
                }[];
            }) => {
                await apiODIN.editCellValues({ extractionId, cells });
            },
            onSuccess: (_data, { extractionId }) => {
                invalidateExtraction({ extractionId });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    kind: "toast",
                    title: "Failed to update cell values",
                    severity: "danger",
                });
            },
        });

    const useRevertCellValuesMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.revertCellValues,
            mutationFn: async ({
                extractionId,
                cells,
            }: {
                extractionId: number;
                cells: { rowId: number; columnId: number }[];
            }) => {
                await apiODIN.revertCellValues({ extractionId, cells });
            },
            onSuccess: (_data, { extractionId }) => {
                invalidateExtraction({ extractionId });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    kind: "toast",
                    title: "Failed to revert cell values",
                    severity: "danger",
                });
            },
        });

    const useResetRowChangesMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.resetRowChanges,
            mutationFn: async ({
                extractionId,
                rowIds,
            }: {
                extractionId: number;
                rowIds: number[];
            }) => {
                await apiODIN.resetRowChanges({ extractionId, rowIds });
            },
            onSuccess: (_data, { extractionId }) => {
                invalidateExtraction({ extractionId });
            },
        });

    const useSetColumnStateMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.setColumnState,
            mutationFn: async ({
                extractionId,
                state,
                columnId,
            }: {
                extractionId: number;
                state: boolean;
                columnId?: number;
            }) => {
                await apiODIN.setColumnState({
                    extractionId,
                    state,
                    columnId,
                });
            },
            onSuccess: (_data, { extractionId }) => {
                invalidateExtraction({ extractionId });
            },
        });

    const useBatchSetColumnStateMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.batchSetColumnState,
            mutationFn: async ({
                extractionId,
                columnIds,
                state,
            }: {
                extractionId: number;
                columnIds: number[];
                state: boolean;
            }) => {
                const results = await Promise.allSettled(
                    columnIds.map(columnId =>
                        apiODIN.setColumnState({
                            extractionId,
                            state,
                            columnId,
                        }),
                    ),
                );
                const failures = results.filter(
                    result => result.status === "rejected",
                ).length;
                return {
                    failures,
                    successes: results.length - failures,
                };
            },
            onSuccess: ({ successes, failures }, { extractionId }) => {
                if (successes > 0) {
                    invalidateExtraction({ extractionId });
                }
                if (failures > 0) {
                    addNotificationWithDefaultTimeout({
                        title:
                            successes > 0
                                ? `Failed to update ${failures} of ${failures + successes} columns`
                                : "Failed to update columns",
                        kind: "toast",
                        severity: "danger",
                    });
                }
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to update columns",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useConvertExtractionsToDraftMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.convertToDraft,
            mutationFn: async ({
                extractionId,
                extractionIds,
                projectId,
                databaseId,
            }: {
                extractionId: number;
                extractionIds: number[];
                projectId: number;
                databaseId: number;
            }) => {
                const response = await apiODIN.convertExtractionsToDraft({
                    extractionId,
                    extractionIds,
                    projectId,
                    databaseId,
                });
                return response.data;
            },
            onSuccess: (_data, { extractionIds }) => {
                for (const id of extractionIds) {
                    invalidateExtraction({
                        extractionId: id,
                        includeConflicts: true,
                    });
                }
                void queryClient.invalidateQueries({
                    queryKey: projectKeys.all,
                });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to convert extraction(s) to draft",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useDismissOutliersMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.dismissOutliers,
            mutationFn: async ({
                extractionId,
                outliers,
            }: {
                extractionId: number;
                outliers: {
                    rowOrder: number;
                    periodId: number;
                    accountLabel: string;
                    dismissed: boolean;
                }[];
            }) => {
                await apiODIN.dismissOutliers(extractionId, outliers);
            },
            onSuccess: (_data, { extractionId }) => {
                invalidateExtraction({ extractionId });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to update outlier dismissals",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useLlmReExtractMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.llmReExtract,
            mutationFn: async ({
                extractionId,
                onProgress,
            }: {
                extractionId: number;
                onProgress?: (params: {
                    percentage: number;
                    message: string;
                }) => void;
            }) => {
                await apiODIN.llmReExtract({
                    extractionId,
                    onProgress,
                });
            },
            onSuccess: (_data, { extractionId }) => {
                invalidateExtraction({
                    extractionId,
                    includeLabelset: true,
                    includeExtractionType: true,
                });
            },
        });

    const useDismissLlmDiagnosesMutation = () =>
        useMutation({
            mutationKey: extractionMutationKeys.dismissLlmDiagnoses,
            mutationFn: async ({
                extractionId,
                groups,
            }: {
                extractionId: number;
                groups: { checkIdentities: string[]; dismissed: boolean }[];
            }) => {
                await apiODIN.dismissLlmDiagnoses(extractionId, groups);
            },
            onSuccess: (_data, { extractionId }) => {
                invalidateExtraction({ extractionId });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    kind: "toast",
                    title: "Failed to dismiss diagnosis",
                    severity: "danger",
                });
            },
        });

    return {
        useChangeTableMutation,
        useSaveMappingsMutation,
        useSaveLabelsetMutation,
        useDiscardExtractionMutation,
        useUpdateExtractionTypeMutation,
        useChangeRowTypeMutation,
        useFlipRowSignMutation,
        useSetDocumentTargetRowMutation,
        useEditCellValuesMutation,
        useRevertCellValuesMutation,
        useResetRowChangesMutation,
        useSetColumnStateMutation,
        useBatchSetColumnStateMutation,
        useConvertExtractionsToDraftMutation,
        useLlmReExtractMutation,
        useDismissOutliersMutation,
        useDismissLlmDiagnosesMutation,
        extractionMutationKeys,
    };
};

export type TExtractionMutationHooks = ReturnType<
    typeof createExtractionMutationHooks
>;
